import React, { useState } from 'react';
import { Play, X } from 'lucide-react';
import { FLAVOURS, FLAVOUR_ORDER } from '../../data/mock';
import { useFlavour } from '../../context/FlavourContext';
import Reveal from '../../components/Reveal';
import CinematicClip from '../../components/CinematicClip';
import { track } from '../../lib/analytics';

function PlayBadge({ size = 'md', color }) {
  const d = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
  return (
    <span className={`relative flex items-center justify-center ${d} rounded-full`} style={{ background: `rgba(${color.rgb},0.18)`, border: `1px solid rgba(${color.rgb},0.65)` }}>
      <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `rgba(${color.rgb},0.22)` }} />
      <Play className={size === 'lg' ? 'w-8 h-8' : 'w-5 h-5'} style={{ color: color.primary }} fill="currentColor" />
    </span>
  );
}

function ProgressBar({ color, duration = 8 }) {
  return (
    <div className="h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
      <div className="clip-progress h-full rounded-full" style={{ background: color.primary, animationDuration: `${duration}s` }} />
    </div>
  );
}

function ClipModal({ flavour, onClose }) {
  if (!flavour) return null;
  return (
    <div data-testid="clip-modal" className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/90 backdrop-blur-md" onClick={onClose}>
      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <button data-testid="clip-modal-close" onClick={onClose} className="absolute -top-11 right-0 text-white/70 hover:text-white flex items-center gap-2 font-cond tracking-wide">
          <X className="w-6 h-6" /> CLOSE
        </button>
        <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl" style={{ aspectRatio: '16 / 9' }}>
          <CinematicClip flavour={flavour} rounded="rounded-2xl" />
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10 pointer-events-none">
            <p className="font-cond tracking-[0.25em] text-white/70 text-sm">S-MAN · {flavour.name}</p>
            <h3 className="font-display text-3xl md:text-6xl mt-1" style={{ color: '#fff' }}>FEEL THE <span style={{ color: flavour.primary }}>ENERGY.</span></h3>
            <div className="mt-5 flex items-center gap-4">
              <PlayBadge color={flavour} />
              <div className="flex-1"><ProgressBar color={flavour} duration={8} /></div>
              <span className="font-cond text-white/60 text-sm">0:08</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VideoSection() {
  const { flavour } = useFlavour();
  const [active, setActive] = useState(null);
  const open = (fl) => { setActive(fl); track('video_play', { flavour: fl.id }); };

  return (
    <section className="relative py-20 md:py-28 bg-[#050505]">
      <div className="max-w-[1400px] mx-auto px-5">
        <Reveal className="text-center mb-12">
          <p className="font-cond tracking-[0.3em] text-white/40 text-sm mb-3">S-MAN ON SCREEN</p>
          <h2 className="font-display text-4xl md:text-6xl">SEE IT.<br /><span className="text-flavour flavour-transition">FEEL IT.</span></h2>
        </Reveal>

        {/* feature clip = currently selected flavour */}
        <Reveal>
          <button data-testid="video-feature" onClick={() => open(flavour)} className="group relative w-full rounded-3xl overflow-hidden border border-white/10 block text-left" style={{ aspectRatio: '21 / 9' }}>
            <CinematicClip flavour={flavour} rounded="rounded-3xl" className="transition-transform duration-700 group-hover:scale-[1.03]" />
            <div className="relative h-full flex flex-col justify-end p-6 md:p-12">
              <div className="flex items-center gap-4">
                <PlayBadge size="lg" color={flavour} />
                <div>
                  <p className="font-cond tracking-[0.25em] text-white/60 text-sm">BRAND FILM · 0:08 · {flavour.name}</p>
                  <h3 className="font-display text-3xl md:text-6xl mt-1">FEEL THE <span style={{ color: flavour.primary }}>ENERGY.</span></h3>
                </div>
              </div>
              <p className="text-white/60 mt-4 max-w-lg">A cinematic look at confidence, attraction and performance.</p>
              <div className="mt-5 max-w-md"><ProgressBar color={flavour} duration={8} /></div>
            </div>
          </button>
        </Reveal>

        {/* per-flavour clip strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
          {FLAVOUR_ORDER.map((id, i) => {
            const fl = FLAVOURS[id];
            return (
              <Reveal key={id} delay={i * 70}>
                <button data-testid={`video-clip-${id}`} onClick={() => open(fl)} className="group relative w-full rounded-2xl overflow-hidden border border-white/10 block text-left" style={{ aspectRatio: '3 / 4' }}>
                  <CinematicClip flavour={fl} rounded="rounded-2xl" className="transition-transform duration-700 group-hover:scale-[1.05]" />
                  <div className="absolute top-3 left-3"><PlayBadge color={fl} /></div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="font-cond tracking-[0.2em] text-white/60 text-[10px]">0:08</p>
                    <h4 className="font-display text-lg leading-tight" style={{ color: fl.primary }}>{fl.name}</h4>
                    <div className="mt-2"><ProgressBar color={fl} duration={7 + (i % 3)} /></div>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      {active && <ClipModal flavour={active} onClose={() => setActive(null)} />}
    </section>
  );
}
